import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  DeleteReofferBmkResult,
  ReofferBmk,
  ReofferBmkApiItem,
  UpsertReofferBmkRequest,
} from './reoffer-bmk.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class ReofferBmkService {
  private readonly endpoint = apiUrl('reofferbmk');

  constructor(private readonly http: HttpClient) {}

  getReofferBmks(): Observable<ReofferBmk[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeReofferBmk(item))),
    );
  }

  createReofferBmk(payload: UpsertReofferBmkRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateReofferBmk(id: number, payload: UpsertReofferBmkRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteReofferBmk(id: number): Observable<DeleteReofferBmkResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteReofferBmkResult => ({ kind: 'success' })),
      catchError(() => of<DeleteReofferBmkResult>({
        kind: 'error',
        message: 'Unable to delete reoffer bmk at this time. Please try again.',
      })),
    );
  }

  private normalizeReofferBmk(item: ReofferBmkApiItem): ReofferBmk {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: ReofferBmkApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): ReofferBmkApiItem[] {
    if (Array.isArray(response)) {
      return response as ReofferBmkApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as ReofferBmkApiItem[];
      }

      if (Array.isArray(items)) {
        return items as ReofferBmkApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
